import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, FileSpreadsheet, Download, Pencil, Trash2, ShoppingCart, ArrowLeft } from "lucide-react";
import { CategoryGrid } from "../components/CategoryGrid";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PageHeader, ProductImage } from "../components/Bits";
import { ProductFormDialog, ExcelImportDialog } from "./ProductDialogs";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api, errMsg, money, downloadFile, matches } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SORTS = {
  name_asc: ["Ad (A-Z)", (a, b) => a.name.localeCompare(b.name, "tr")],
  name_desc: ["Ad (Z-A)", (a, b) => b.name.localeCompare(a.name, "tr")],
  code_asc: ["Ürün Kodu (A-Z)", (a, b) => a.code.localeCompare(b.code, "tr", { numeric: true })],
  code_desc: ["Ürün Kodu (Z-A)", (a, b) => b.code.localeCompare(a.code, "tr", { numeric: true })],
  price_asc: ["Fiyat (Artan)", (a, b) => a.price - b.price],
  price_desc: ["Fiyat (Azalan)", (a, b) => b.price - a.price],
  stock_asc: ["Stok (Az → Çok)", (a, b) => a.stock - b.stock],
  stock_desc: ["Stok (Çok → Az)", (a, b) => b.stock - a.stock],
  newest: ["En Yeni", (a, b) => (b.created_at || "").localeCompare(a.created_at || "")],
};

export default function ProductsPage() {
  const { user } = useAuth();
  const { add } = useCart();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const loadCats = () => api.get("/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e)));
  const load = () => { loadCats(); return api.get("/products").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(() => { load(); }, []);

  const [sort, setSort] = useState(() => localStorage.getItem("productSort") || "name_asc");
  const [reorder, setReorder] = useState(false);
  useEffect(() => localStorage.setItem("productSort", sort), [sort]);
  const shown = useMemo(() => items.filter((p) => (cat === null || (p.category || "") === cat) &&
      matches([p.name, p.code, p.category, p.description], q)).sort(SORTS[sort][1]), [items, q, cat, sort]);

  const del = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Ürün silindi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Ürün Kataloğu" kicker={`${items.length} ürün`}>
        {isAdmin && <>
          <Button data-testid="products-export-button" variant="outline" onClick={() => downloadFile("/products/excel/export", "urunler.xlsx")}><Download className="mr-2 h-4 w-4" />Excel'e Aktar</Button>
          <Button data-testid="products-import-button" variant="outline" onClick={() => setImportOpen(true)}><FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-600" />Excel'den Yükle</Button>
          <Button data-testid="products-add-button" className="bg-slate-950 hover:bg-blue-600" onClick={() => { setEdit(null); setFormOpen(true); }}><Plus className="mr-2 h-4 w-4" />Yeni Ürün</Button>
        </>}
      </PageHeader>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="products-search-input" placeholder={cat === null ? "Tüm ürünlerde ara (ad, kod, kategori)..." : "Bu kategoride ara..."} value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-white pl-10" />
        </div>
        {(cat !== null || q) && (
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-full bg-white sm:w-56" data-testid="products-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{Object.entries(SORTS).map(([k, [l]]) => <SelectItem key={k} value={k} data-testid={`products-sort-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {isAdmin && cat === null && !q && cats.length > 1 && (
          <Button data-testid="categories-reorder-toggle" variant={reorder ? "default" : "outline"} className={`h-11 ${reorder ? "bg-blue-600 hover:bg-blue-700" : ""}`} onClick={() => setReorder(!reorder)}>
            {reorder ? "Sıralamayı Bitir" : "Kategori Sırasını Düzenle"}
          </Button>
        )}
      </div>

      {cat !== null && (
        <div className="mb-6 flex items-center gap-3 fade-up">
          <Button data-testid="products-back-to-categories-button" variant="outline" onClick={() => { setCat(null); setQ(""); }}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Kategoriler
          </Button>
          <h2 className="font-display text-xl font-semibold" data-testid="products-current-category">{cat || "Diğer"}</h2>
        </div>
      )}

      {cat === null && !q && items.length > 0 ? (
        <CategoryGrid categories={cats} isAdmin={isAdmin} reorder={reorder} setCategories={setCats} onOpen={setCat} onChanged={loadCats} />
      ) : shown.length === 0 ? (
        <div data-testid="products-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-16 text-center text-slate-500">
          {items.length === 0 ? (isAdmin ? "Henüz ürün yok. Excel'den yükleyin veya yeni ürün ekleyin." : "Henüz ürün eklenmemiş.") : "Aramanızla eşleşen ürün bulunamadı."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((p, i) => (
            <div key={p.id} data-testid={`product-card-${p.code}`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
              className="fade-up group flex flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-shadow hover:shadow-md">
              <ProductImage product={p} className="aspect-[4/3] w-full" />
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-wider text-slate-500">
                  <span>{p.code}</span><span>{p.category}</span>
                </div>
                <h3 className="mt-1 font-display font-semibold leading-snug text-slate-950">{p.name}</h3>
                {p.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
                <div className="mt-auto pt-4 flex items-end justify-between">
                  <div>
                    <p className="font-display text-xl font-bold" data-testid={`product-price-${p.code}`}>{money(p.price)}</p>
                    <p className="text-[11px] text-slate-500">/{p.unit} · KDV %{p.vat} · Stok: <span className={p.stock <= 0 ? "text-rose-600 font-semibold" : ""}>{p.stock}</span></p>
                  </div>
                  <div className="flex gap-1">
                    {isAdmin && <>
                      <button data-testid={`product-edit-${p.code}`} onClick={() => { setEdit(p); setFormOpen(true); }} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`product-delete-${p.code}`} onClick={() => del(p)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                    </>}
                    <button data-testid={`product-add-cart-${p.code}`} onClick={() => { add(p); toast.success(`${p.name} siparişe eklendi`); }} className="grid h-9 w-9 place-items-center rounded-lg bg-blue-600 text-white transition-transform active:scale-95 hover:bg-blue-700"><ShoppingCart className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ProductFormDialog open={formOpen} onOpenChange={setFormOpen} product={edit} onSaved={load} />
      <ExcelImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}
